import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useAppContext } from '../../../../../store/AppContext';
import adminAccountSchema from '../../../../../validations/administration/adminAccountSchema';
import useModal from '../../../../../utils/hooks/useModal';
import useNotification from '../../../../../utils/hooks/useNotification';

// Services
import useAdministratorsService from '../../../../../services/administratorsService';

// Components
import FlashMessage from '../../../../molecules/FlashMessage/FlashMessage';
import SelectField from '../../../../atoms/SelectField/SelectField';
import InputField from '../../../../atoms/InputField/InputField';
import adminTypes from '../../../../../constants/adminTypes';

const CreationForm = forwardRef(({
  tableType, roles, establishmentId, setAdministrators,
}, ref) => {
  const { t } = useTranslation();
  const [{ adminType }, stateDispatch] = useAppContext();

  const { closeModal } = useModal();
  const { displayNotification } = useNotification();

  const { postAdminAccount } = useAdministratorsService();

  const rolesOptions = useMemo(() => (roles?.map((role) => (
    { label: role.name, value: role.id, isSuperAdmin: role.isSuperAdmin }
  )) || []), [roles]);

  const { isLoading: postAccountLoading, mutate: postAdminAccountMutate, error } = useMutation(
    (values) => postAdminAccount({ type: tableType, establishmentId, values }),
    {
      onSuccess: (res) => {
        closeModal();
        if (res?.data) {
          setAdministrators((state) => state.concat([res.data]));
        }

        displayNotification({
          message: adminType === adminTypes.platform
            ? t('admin.creation.successInvited', {
              email: res.data.emailAddress,
            })
            : t('admin.creation.success', {
              name: `${res.data.firstName} ${res.data.lastName}`,
            }),
        });
      },
    },
  );

  useEffect(() => {
    stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: postAccountLoading ? 'postAccount' : null });
  }, [postAccountLoading]);

  const formik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      role: rolesOptions?.find((option) => option.isSuperAdmin) || null,
    },
    onSubmit: (values) => {
      postAdminAccountMutate({
        emailAddress: values.email,
        firstName: values.firstname,
        lastName: values.lastname,
        roleId: values.role?.value,
      });
    },
    validationSchema: adminAccountSchema,
  });

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        formik.handleSubmit();
      },
    }),
  );

  const getInput = useCallback((name) => (
    <div className="m-b-15">
      <InputField
        id={`admin_creation_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`admin.creation.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
      />
    </div>
  ), [formik]);

  return (
    <form>
      {getInput('firstname')}
      {getInput('lastname')}
      {getInput('email')}

      <SelectField
        id="admin_creation_roles"
        name="role"
        options={rolesOptions}
        label={t('admin.creation.form.role')}
        error={formik.errors?.role && formik.touched?.role ? formik.errors.role : null}
        setFieldTouched={formik.setFieldTouched}
        setFieldValue={formik.setFieldValue}
        value={formik.values.role}
        isRequired
        // [rule] => if i'm a platform admin creating an establishment admin,
        // role is always super admin so i can't change it (= disabled)
        disabled={formik.values.role && adminType === adminTypes.platform && tableType === adminTypes.establishment}
      />

      {error ? (
        <div className="m-t-20">
          <FlashMessage
            color="error"
            message={t(`code_errors.${error?.response?.data?.code || 'generic'}`)}
          />
        </div>
      ) : null}
    </form>
  );
});

CreationForm.propTypes = {
  tableType: PropTypes.string.isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({})),
  establishmentId: PropTypes.string,
  setAdministrators: PropTypes.func,
};

CreationForm.defaultProps = {
  roles: [],
  establishmentId: null,
  setAdministrators: () => {},
};

export default CreationForm;
